import type {MouseEvent, ReactElement} from 'react';
import {useEffect, useState} from 'react';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {Button, Size, Type} from '@Components/button';
import {Text, TextSize} from '@Components/text';
import {isIOSApp} from '@Utils/browser.util';
import useWindowSize from '@Hooks/useWindowSize';
import {openPremiumPricingModal} from '@Modals/premium-pricing-modal/premium-pricing-modal.library';
import {openErrorModal} from '@Modals/error-modal';
import type {CheckoutCreditPack, CheckoutStepProps, CreditPackPurchaseResult, IapTransaction, IosCreditPackPrice} from '@Components/checkout-flow/checkout-flow.types';
import {CheckoutMode, CheckoutStep, IAP_CREDITS_PRODUCT_TYPE, POPULAR_CREDIT_PACK_QUANTITY} from '@Components/checkout-flow/checkout-flow.types';
import {selectCreditPack, setCreditBenefitsViewOpen, setIosCreditPackPrices} from '@Components/checkout-flow/checkout-flow-reducer';
import {formatCreditPackPrice, getCheckoutModalTitle, isCheckoutBottomSheet, isCompactCheckoutLayout} from '@Components/checkout-flow/checkout-flow.library';
import {CreditPackListItem} from '@Components/checkout-flow/components/credit-pack-list-item/credit-pack-list-item';
import {CreditBenefitsPanel} from '@Components/checkout-flow/components/credit-benefits-panel/credit-benefits-panel';
import {CheckoutInfoMessage} from '@Components/checkout-flow/components/checkout-info-message/checkout-info-message';
import {UPGRADE_FOR_CREDITS_TRIGGER_SELECTOR} from '@Components/checkout-flow/components/credit-packs-step/credit-packs-step.types';
import {CheckoutTestId} from '@/test-ids/checkout-test-id';
import styles from './credit-packs-step.module.scss';

export function CreditPacksStep({request, controller}: CheckoutStepProps): ReactElement | null {
  const dispatch = useAppDispatch();
  const dialogData = useAppSelector((state) => state.checkoutFlow.dialogData);
  const selectedCreditPackPriceId = useAppSelector((state) => state.checkoutFlow.selectedCreditPackPriceId);
  const iosCreditPackPrices = useAppSelector((state) => state.checkoutFlow.iosCreditPackPrices);
  const isBenefitsViewOpen = useAppSelector((state) => state.checkoutFlow.isCreditBenefitsViewOpen);
  const {windowWidth} = useWindowSize();
  const hasIosPricing = Object.keys(iosCreditPackPrices).length > 0;
  const [isIosPricingLoaded, setIsIosPricingLoaded] = useState(!isIOSApp() || hasIosPricing);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const isCompact = isCompactCheckoutLayout(windowWidth);
  const isBottomSheet = isCheckoutBottomSheet(windowWidth);

  // The step remounts when the compact benefits view slides in and out, and the store prices outlive that.
  useEffect(() => {
    if (!isIOSApp() || hasIosPricing) {
      return;
    }
    window.PMW.getIAPProducts(IAP_CREDITS_PRODUCT_TYPE, async (items): Promise<void> => {
      const pricesByIndex: Record<number, IosCreditPackPrice> = {};
      for (const item of items) {
        pricesByIndex[item.index] = {priceId: item.id, displayPrice: item.displayPrice, currencyCode: item.currencyCode};
      }
      dispatch(setIosCreditPackPrices(pricesByIndex));
      setIsIosPricingLoaded(true);
      return Promise.resolve();
    });
  }, []);

  if (dialogData === null || request.mode !== CheckoutMode.CREDIT_PACK) {
    return null;
  }

  const selectedIndex = dialogData.creditPacks.findIndex((creditPack) => creditPack.priceId === selectedCreditPackPriceId);
  const selectedCreditPack = selectedIndex === -1 ? null : dialogData.creditPacks[selectedIndex];

  const onHeadingTextClick = (event: MouseEvent<HTMLDivElement>): void => {
    if (!(event.target instanceof Element) || event.target.closest(UPGRADE_FOR_CREDITS_TRIGGER_SELECTOR) === null) {
      return;
    }
    openPremiumPricingModal(false);
  };

  const purchaseThroughIap = (): void => {
    const iosPrice = iosCreditPackPrices[selectedIndex];
    if (iosPrice === undefined) {
      return;
    }
    setIsPurchasing(true);
    window.PMW.callBackForIAPurchase(
      iosPrice.priceId,
      iosPrice.priceId,
      IAP_CREDITS_PRODUCT_TYPE,
      (transaction: IapTransaction): void => {
        void confirmIapPurchase(transaction);
      },
      (showError: boolean): void => {
        setIsPurchasing(false);
        if (showError) {
          openErrorModal({log: false});
        }
      }
    );
  };

  const confirmIapPurchase = async (transaction: IapTransaction): Promise<void> => {
    try {
      const response: unknown = await window.PMW.pollingAjaxCallAsync(
        () => Promise.resolve(),
        () => window.PMW.readLocal('premium/hasSuccessfulIAPCreditPackPurchaseProcessed', {idTransaction: transaction.idTransaction})
      );
      request.onSuccess?.(response as CreditPackPurchaseResult);
      controller.exit();
    } catch {
      openErrorModal({log: false});
    } finally {
      setIsPurchasing(false);
    }
  };

  const onContinue = (): void => {
    if (isIOSApp()) {
      purchaseThroughIap();
      return;
    }
    controller.goToStep(CheckoutStep.STRIPE_EMBEDDED);
  };

  const getFormattedPrice = (index: number): string => {
    const iosPrice = iosCreditPackPrices[index];
    if (iosPrice !== undefined) {
      return iosPrice.displayPrice;
    }
    return formatCreditPackPrice(dialogData.creditPacks[index].amount, dialogData.currencyCode);
  };

  const getFormattedPricePerCredit = (creditPack: CheckoutCreditPack, index: number): string | null => {
    if (iosCreditPackPrices[index] !== undefined || creditPack.quantity === 0) {
      return null;
    }
    return window.i18next.t('pmwjs_x_per_credit', {price: window.PMW.util.currencyFormat(creditPack.amount / creditPack.quantity, dialogData.currencyCode)});
  };

  const getHeadingText = (): ReactElement | null => {
    if (request.headingText === '') {
      return null;
    }
    return (
      <div className={styles.headingText} onClick={onHeadingTextClick} role="presentation">
        <Text val={request.headingText} size={TextSize.SM} className="content-body" dangerouslySetInnerHTML />
      </div>
    );
  };

  const getTitle = (): ReactElement | null => {
    if (isCompact) {
      return null;
    }
    return <Text val={getCheckoutModalTitle(request, CheckoutStep.CREDIT_PACKS)} size={TextSize.LG} bold className={`ct-neutral-primary ${styles.title}`} />;
  };

  const getCreditPackList = (): ReactElement | null => {
    if (!isIosPricingLoaded) {
      return null;
    }
    return (
      <ul className={styles.list} role="radiogroup">
        {dialogData.creditPacks.map((creditPack, index) => {
          return (
            <CreditPackListItem
              key={creditPack.id}
              creditPack={creditPack}
              formattedPrice={getFormattedPrice(index)}
              formattedPricePerCredit={getFormattedPricePerCredit(creditPack, index)}
              isPopular={creditPack.quantity === POPULAR_CREDIT_PACK_QUANTITY}
              showsPopularLabel={!isBottomSheet}
              isSelected={index === selectedIndex}
              onSelect={(): void => {
                dispatch(selectCreditPack(creditPack.priceId));
              }}
            />
          );
        })}
      </ul>
    );
  };

  const getBenefitsButton = (): ReactElement | null => {
    if (!isCompact) {
      return null;
    }
    return (
      <Button
        type={Type.SECONDARY}
        size={Size.SMALL}
        text={window.i18next.t('pmwjs_what_can_i_use_credits_for')}
        customClasses="-noun"
        onClick={(): void => {
          dispatch(setCreditBenefitsViewOpen(true));
        }}
      />
    );
  };

  const getPacksColumn = (): ReactElement => {
    return (
      <div className={styles.packsColumn}>
        <div className={styles.packsContent}>
          {getTitle()}
          {getHeadingText()}
          {getCreditPackList()}
          <CheckoutInfoMessage message={window.i18next.t('pmwjs_purchased_credits_do_not_expire')} className={styles.expiryNote} />
        </div>
        <div className={`${styles.footer} ${isCompact ? styles.footerCompact : ''}`}>
          {getBenefitsButton()}
          <Button type={Type.PRIMARY} size={Size.SMALL} text={window.i18next.t('pmwjs_continue')} isLoading={isPurchasing} disabled={isPurchasing || selectedCreditPack === null} onClick={onContinue} />
        </div>
      </div>
    );
  };

  const getBenefitsColumn = (): ReactElement | null => {
    if (isCompact) {
      return null;
    }
    return <CreditBenefitsPanel isCompact={false} selectedCreditPack={selectedCreditPack} formattedPrice="" isPurchasing={false} />;
  };

  if (isCompact && isBenefitsViewOpen) {
    return (
      <CreditBenefitsPanel
        isCompact
        selectedCreditPack={selectedCreditPack}
        formattedPrice={selectedIndex === -1 ? '' : getFormattedPrice(selectedIndex)}
        isPurchasing={isPurchasing}
        onBack={(): void => {
          dispatch(setCreditBenefitsViewOpen(false));
        }}
        onClose={controller.exit}
        onContinue={onContinue}
      />
    );
  }

  return (
    <div className={`${styles.container} ${isCompact ? styles.containerCompact : ''}`} data-testid={CheckoutTestId.CREDIT_PACKS_STEP}>
      {getPacksColumn()}
      {getBenefitsColumn()}
    </div>
  );
}
